import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

// ============================================================
// The day, written out
// ============================================================
// Reps were retyping their day into WhatsApp every evening from the same visits
// they had already logged. This shows the report the server generates from those
// visits, so the evening message is a copy rather than a rewrite — and so the
// version the manager reads is the version the CRM actually holds.
//
// The text is deliberately not editable here. If a line is wrong, the visit is
// wrong, and fixing the visit is what makes tomorrow's report right too.

const MANAGER_ROLES = ["manager", "team_leader", "admin", "superadmin", "executive"];

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

export default function DailyVisitReport({ user, accent = "#0f9f9a", teamOnly = false }) {
  const effectiveRole = user?.role === "superadmin"
    ? "superadmin"
    : (user?.app_role_overrides?.crm || user?.role);
  const canSeeTeam = MANAGER_ROLES.includes(effectiveRole);

  const [scope, setScope]   = useState(teamOnly ? "team" : "mine");
  const [date, setDate]     = useState(todayIST());
  const [loading, setLoad]  = useState(true);
  const [report, setReport] = useState(null);
  const [team, setTeam]     = useState(null);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      if (scope === "team") {
        const res = await API.get(`/reports/visit-report/team?date=${date}`);
        setTeam(res.data);
      } else {
        const res = await API.get(`/reports/visit-report?date=${date}`);
        setReport(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load the report");
      if (scope === "team") setTeam(null); else setReport(null);
    } finally {
      setLoad(false);
    }
  }, [scope, date]);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy — select the text and copy it manually");
    }
  };

  const share = async (text) => {
    if (navigator.share) {
      try { await navigator.share({ text }); return; } catch { /* dismissed */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const card = {
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: 0, overflow: "hidden", marginBottom: 12,
  };
  const pre = {
    margin: 0, padding: "15px 16px", fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
    fontSize: 12.5, lineHeight: 1.7, color: "#1e293b", whiteSpace: "pre-wrap",
    wordBreak: "break-word", background: "#fbfdfe",
  };
  const btn = (filled) => ({
    padding: "8px 13px", borderRadius: 9, fontSize: 12.5, fontWeight: 750, cursor: "pointer",
    fontFamily: "inherit", border: filled ? 0 : "1px solid #cbd5e1",
    background: filled ? accent : "#fff", color: filled ? "#fff" : "#475569",
  });
  const chip = (on) => ({
    padding: "7px 14px", borderRadius: 999, fontSize: 12.5, fontWeight: 750, cursor: "pointer",
    fontFamily: "inherit", border: `1.5px solid ${on ? accent : "#e2e8f0"}`,
    background: on ? accent : "#fff", color: on ? "#fff" : "#475569",
  });

  const actions = (text, label) => (
    <div style={{ display: "flex", gap: 8, padding: "11px 16px", borderTop: "1px solid #eef2f7", background: "#fff" }}>
      <button type="button" style={btn(true)} onClick={() => copy(text, label)}>Copy text</button>
      <button type="button" style={btn(false)} onClick={() => share(text)}>Share</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "4px 2px 24px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 19, fontWeight: 800, color: "#0f172a" }}>{teamOnly ? "Team day report" : "Day report"}</h2>
      <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "#64748b", lineHeight: 1.6 }}>
        {teamOnly
          ? "Manager view of employee visit reports generated from CRM activity."
          : "Built from the visits you logged. It is sent to you at 7:30 pm — this is the same text, available any time."}
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 13 }}>
        {canSeeTeam && !teamOnly && (
          <>
            <button type="button" style={chip(scope === "mine")} onClick={() => setScope("mine")}>My day</button>
            <button type="button" style={chip(scope === "team")} onClick={() => setScope("team")}>My team</button>
          </>
        )}
        <input
          type="date" value={date} max={todayIST()}
          onChange={e => setDate(e.target.value || todayIST())}
          style={{ padding: "8px 11px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 13, fontFamily: "inherit" }}
        />
      </div>

      {loading ? (
        <div style={{ ...card, padding: 22, textAlign: "center", color: "#64748b", fontSize: 13 }}>
          Building the report...
        </div>
      ) : scope === "team" ? (
        !team || !team.reports.length ? (
          <div style={{ ...card, padding: 20, color: "#64748b", fontSize: 13, lineHeight: 1.6 }}>
            No reps report to you on this date, so there is nothing to roll up.
          </div>
        ) : (
          <>
            <div style={{ ...card, padding: "14px 16px", display: "flex", gap: 18, flexWrap: "wrap" }}>
              {[
                ["Visits", team.totals.visits],
                ["Reached management", team.totals.management],
                ["Stopped at reception", team.totals.reception],
                ["Logged nothing", team.totals.silent],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: 21, fontWeight: 800, color: label === "Logged nothing" && value ? "#b45309" : accent }}>{value}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{label}</div>
                </div>
              ))}
            </div>
            {team.reports.map(r => (
              <div key={r.user_id} style={card}>
                <pre style={pre}>{r.body_text}</pre>
                {actions(r.body_text, `${r.user_name}'s report`)}
              </div>
            ))}
          </>
        )
      ) : !report ? (
        <div style={{ ...card, padding: 20, color: "#64748b", fontSize: 13 }}>No report available for this date.</div>
      ) : (
        <div style={card}>
          <pre style={pre}>{report.body_text}</pre>
          {actions(report.body_text, "Report")}
        </div>
      )}
    </div>
  );
}
